import api from "../../../../services/api";

const getSignature = () => localStorage.getItem("signature");

const post = async (path: string, body: Record<string, any>) => {
  const response = await api.post(path, {
    signature: getSignature(),
    ...body,
  });
  return response.data?.data;
};

export const listProjects = async (
  companyId: string,
  companyType: string,
  filters: {
    search?: string;
    status?: number | null;
    priority?: number | null;
    page?: number;
    limit?: number;
  } = {},
) =>
  post("projectService/project/listProjects", {
    company_id: companyId,
    company_type: companyType,
    ...filters,
  });

export const getProjectById = async (
  companyId: string,
  companyType: string,
  projectId: number,
) =>
  post("projectService/project/getProjectById", {
    company_id: companyId,
    company_type: companyType,
    id: projectId,
  });

export const getProjectByKey = async (
  companyId: string,
  companyType: string,
  projectKey: string,
) =>
  post("projectService/project/getProjectById", {
    company_id: companyId,
    company_type: companyType,
    project_key: projectKey,
  });

export const createProject = async (
  companyId: string,
  companyType: string,
  payload: Record<string, any>,
) =>
  post("projectService/project/createProject", {
    company_id: companyId,
    company_type: companyType,
    ...payload,
  });

export const updateProject = async (
  companyId: string,
  companyType: string,
  projectId: number,
  payload: Record<string, any>,
) =>
  post("projectService/project/updateProject", {
    company_id: companyId,
    company_type: companyType,
    id: projectId,
    ...payload,
  });

export const deleteProject = async (
  companyId: string,
  companyType: string,
  projectId: number,
) =>
  post("projectService/project/deleteProject", {
    company_id: companyId,
    company_type: companyType,
    id: projectId,
  });

export const projectKeyAvailable = async (
  companyId: string,
  companyType: string,
  key: string,
) =>
  post("projectService/project/projectKeyAvailable", {
    company_id: companyId,
    company_type: companyType,
    key,
  });

export const listProjectActivity = async (
  companyId: string,
  companyType: string,
  projectId: number,
  page: number = 1,
  limit: number = 10,
  entityType?: string,
  entityId?: number,
) =>
  post("projectService/project/listActivity", {
    company_id: companyId,
    company_type: companyType,
    project_id: projectId,
    page,
    limit,
    ...(entityType ? { entity_type: entityType } : {}),
    ...(entityId ? { entity_id: entityId } : {}),
  });

export const getProjectStats = async (
  companyId: string,
  companyType: string,
  projectId: number,
) =>
  post("projectService/project/getProjectStats", {
    company_id: companyId,
    company_type: companyType,
    id: projectId,
  });

export const listProjectMembers = async (
  companyId: string,
  companyType: string,
  projectId: number,
) =>
  post("projectService/member/listProjectMembers", {
    company_id: companyId,
    company_type: companyType,
    project_id: projectId,
  });

export const addProjectMember = async (
  companyId: string,
  companyType: string,
  projectId: number,
  userId: number,
  role: string,
  userSnapshot: Record<string, any> | null = null,
) =>
  post("projectService/member/addProjectMember", {
    company_id: companyId,
    company_type: companyType,
    project_id: projectId,
    user_id: userId,
    role,
    user_snapshot: userSnapshot,
  });

export const updateProjectMemberRole = async (
  companyId: string,
  companyType: string,
  memberId: number,
  role: string,
) =>
  post("projectService/member/updateProjectMemberRole", {
    company_id: companyId,
    company_type: companyType,
    id: memberId,
    role,
  });

export const removeProjectMember = async (
  companyId: string,
  companyType: string,
  memberId: number,
) =>
  post("projectService/member/removeProjectMember", {
    company_id: companyId,
    company_type: companyType,
    id: memberId,
  });

export const listBoards = async (
  companyId: string,
  companyType: string,
  projectId: number,
) =>
  post("projectService/board/listBoards", {
    company_id: companyId,
    company_type: companyType,
    project_id: projectId,
  });

export const createBoard = async (
  companyId: string,
  companyType: string,
  payload: Record<string, any>,
) =>
  post("projectService/board/createBoard", {
    company_id: companyId,
    company_type: companyType,
    ...payload,
  });

export const updateBoard = async (
  companyId: string,
  companyType: string,
  boardId: number,
  payload: Record<string, any>,
) =>
  post("projectService/board/updateBoard", {
    company_id: companyId,
    company_type: companyType,
    id: boardId,
    ...payload,
  });

export const deleteBoard = async (
  companyId: string,
  companyType: string,
  boardId: number,
) =>
  post("projectService/board/deleteBoard", {
    company_id: companyId,
    company_type: companyType,
    id: boardId,
  });

export const createStatus = async (
  companyId: string,
  companyType: string,
  payload: Record<string, any>,
) =>
  post("projectService/board/createStatus", {
    company_id: companyId,
    company_type: companyType,
    ...payload,
  });

export const updateStatus = async (
  companyId: string,
  companyType: string,
  statusId: number,
  payload: Record<string, any>,
) =>
  post("projectService/board/updateStatus", {
    company_id: companyId,
    company_type: companyType,
    id: statusId,
    ...payload,
  });

export const reorderStatuses = async (
  companyId: string,
  companyType: string,
  boardId: number,
  statusIds: number[],
) =>
  post("projectService/board/reorderStatuses", {
    company_id: companyId,
    company_type: companyType,
    board_id: boardId,
    status_ids: statusIds,
  });

export const deleteStatus = async (
  companyId: string,
  companyType: string,
  statusId: number,
  moveToStatusId: number | null = null,
) =>
  post("projectService/board/deleteStatus", {
    company_id: companyId,
    company_type: companyType,
    id: statusId,
    move_to_status_id: moveToStatusId,
  });

export const listLabels = async (
  companyId: string,
  companyType: string,
  projectId: number,
) =>
  post("projectService/label/listLabels", {
    company_id: companyId,
    company_type: companyType,
    project_id: projectId,
  });

export const createLabel = async (
  companyId: string,
  companyType: string,
  payload: Record<string, any>,
) =>
  post("projectService/label/createLabel", {
    company_id: companyId,
    company_type: companyType,
    ...payload,
  });

export const updateLabel = async (
  companyId: string,
  companyType: string,
  labelId: number,
  payload: Record<string, any>,
) =>
  post("projectService/label/updateLabel", {
    company_id: companyId,
    company_type: companyType,
    id: labelId,
    ...payload,
  });

export const deleteLabel = async (
  companyId: string,
  companyType: string,
  labelId: number,
) =>
  post("projectService/label/deleteLabel", {
    company_id: companyId,
    company_type: companyType,
    id: labelId,
  });

export const listCustomFields = async (
  companyId: string,
  companyType: string,
  projectId: number,
) =>
  post("projectService/customField/listCustomFields", {
    company_id: companyId,
    company_type: companyType,
    project_id: projectId,
  });

export const createCustomField = async (
  companyId: string,
  companyType: string,
  payload: Record<string, any>,
) =>
  post("projectService/customField/createCustomField", {
    company_id: companyId,
    company_type: companyType,
    ...payload,
  });

export const updateCustomField = async (
  companyId: string,
  companyType: string,
  fieldId: number,
  payload: Record<string, any>,
) =>
  post("projectService/customField/updateCustomField", {
    company_id: companyId,
    company_type: companyType,
    id: fieldId,
    ...payload,
  });

export const deleteCustomField = async (
  companyId: string,
  companyType: string,
  fieldId: number,
) =>
  post("projectService/customField/deleteCustomField", {
    company_id: companyId,
    company_type: companyType,
    id: fieldId,
  });
